import { useEffect, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/apiClient';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { businessTypeOptions } from '@/features/settings/mockSettings';
import { ONBOARDING_STATUS_KEY } from '../hooks/useOnboarding';
import { StepFooter } from './StepFooter';

interface ShopConfig {
  businessName: string;
  businessType: string;
  description: string | null;
}

const KNOWN_TYPES = new Set<string>(businessTypeOptions.map((o) => o.value));

export function BusinessStep({ onDone }: { onDone: () => void }) {
  const queryClient = useQueryClient();
  const existing = useQuery({
    queryKey: ['chatbot', 'config'],
    queryFn: () => api.get<ShopConfig | null>('/api/chatbot/config'),
  });

  const [name, setName] = useState('');
  const [type, setType] = useState<string>(businessTypeOptions[0].value);
  const [description, setDescription] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [prefilled, setPrefilled] = useState(false);

  // Prefill once from an existing profile (the wizard re-run, or a profile saved in Settings).
  useEffect(() => {
    if (prefilled || !existing.isSuccess) return;
    const cfg = existing.data;
    if (cfg) {
      setName(cfg.businessName);
      setType(KNOWN_TYPES.has(cfg.businessType) ? cfg.businessType : 'other');
      setDescription(cfg.description ?? '');
    }
    setPrefilled(true);
  }, [existing.isSuccess, existing.data, prefilled]);

  const save = useMutation({
    // POST /chatbot/setup updates only the fields sent, so the catalogue and settings survive a re-run.
    mutationFn: () =>
      api.post('/api/chatbot/setup', {
        businessName: name.trim(),
        businessType: type,
        description: description.trim(),
      }),
    onSuccess: async () => {
      // Wait for the refetch, so the next step already knows the business exists: the channel
      // step's website chat and the knowledge step's free-text box both depend on it.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ONBOARDING_STATUS_KEY }),
        queryClient.invalidateQueries({ queryKey: ['chatbot', 'config'] }),
        queryClient.invalidateQueries({ queryKey: ['settings', 'business'] }),
      ]);
      onDone();
    },
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setError('Business name is required');
      return;
    }
    setError(null);
    save.mutate();
  };

  return (
    <form onSubmit={submit} noValidate>
      <h2 className="text-xl font-semibold text-slate-900 dark:text-white">Tell us about your business</h2>
      <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
        The AI uses this to introduce your business and answer in the right context.
      </p>

      <div className="mt-6 space-y-5">
        <div className="space-y-1.5">
          <Label htmlFor="onb-business-name">Business name</Label>
          <Input
            id="onb-business-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Rahman Fashion House"
            maxLength={120}
            autoFocus
          />
          {error && <p className="text-sm text-error">{error}</p>}
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="onb-business-type">What do you sell?</Label>
          <Select id="onb-business-type" value={type} onChange={(e) => setType(e.target.value)}>
            {businessTypeOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Select>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="onb-business-description">Short description (optional)</Label>
          <Textarea
            id="onb-business-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="e.g. Handmade cotton sarees and panjabis, delivered across Bangladesh."
            maxLength={2000}
            rows={3}
          />
        </div>
      </div>

      <StepFooter onSkip={onDone} primaryLabel="Save and continue" busy={save.isPending} />
    </form>
  );
}
